import {
  FOC_TOUR_LABEL_PLANNING,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';

export const FOC_PLANNING_TOUR_PARAM = 'focPlanningTour';

/** Opens Hub Plan Library and starts the planning-repository walkthrough. */
export const FOC_PLANNING_TOUR_HREF = `/hub?openTab=PlanLibrary&${FOC_PLANNING_TOUR_PARAM}=1`;

export const FOC_PLANNING_ROSTER_TEMPLATES_HREF = `/hub?openTab=RosterTemplates&${FOC_PLANNING_TOUR_PARAM}=rosterTemplates`;

/** Returns to Plan Library for the FOC-ORG-006 preparedness-package modals. */
export const FOC_PLANNING_PACKAGES_HREF = `/hub?openTab=PlanLibrary&${FOC_PLANNING_TOUR_PARAM}=packages`;

export const FOC_REQUIRED_DISPOSITION = 'FOC Required';

export type FocPlanningTourPhase =
  | 'library'
  | 'tasks'
  | 'rosterTemplates'
  | 'roster'
  | 'packages';

export type FocPlanningTourStepId =
  | 'sectionIntro'
  | 'org004'
  | 'org004a'
  | 'org004b'
  | 'org004c'
  | 'org004d'
  | 'org004e'
  | 'sectionImt'
  | 'org005'
  | 'org005a'
  | 'org005b'
  | 'org005c'
  | 'org005d'
  | 'org005e'
  | 'org005f'
  | 'org005g'
  | 'org005h'
  | 'org005i'
  | 'org006'
  | 'org006a'
  | 'org006b'
  | 'org006c'
  | 'org006d'
  | 'org006e'
  | 'org006f'
  | 'org006g'
  | 'org006h'
  | 'org006i'
  | 'org006j'
  | 'focFeedback';

export type FocPlanningTourStepDef = {
  id: FocPlanningTourStepId;
  phase: FocPlanningTourPhase;
  /** Spec requirement id; section intros have none. */
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  /** Optional bullet list rendered under the description in the popover. */
  bullets?: string[];
  /** `data-tour` value. Missing targets become a centered popover. */
  tour?: string;
};

type RequirementInput = Omit<
  FocPlanningTourStepDef,
  'title' | 'requirementId'
> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): FocPlanningTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

const INSTANTIATE_PLAN =
  'Authorized users can use approved repository content to initialize or support an incident, exercise, IAP component, map package, workspace, objective/strategy/tactic library, or other applicable workflow without altering the authoritative source item. Apply Plan copies this plan’s tasks and forms into the workspace; the library plan stays as it is.';

const INSTANTIATE_TEMPLATE =
  'Authorized users can generate a draft activation or recall list from a pre-scripted organization and its associated personnel pool. The user can review primary and alternate personnel before creating exercise or incident assignments or sending authorized notifications. Maintaining a blue-skies staffing plan does not itself activate personnel, create incident seats, or send operational notifications. Load copies this template’s seats into the workspace org chart; the saved template is unchanged, and people are not copied.';

/**
 * Walk order. `org004c` is driven on Tasks when a workspace exists, and as a
 * centered popover on Plan Library. `org005f` is driven on Roster when a
 * workspace exists, and as a centered popover on Roster Templates.
 */
export const FOC_PLANNING_TOUR_STEPS: FocPlanningTourStepDef[] = [
  {
    id: 'sectionIntro',
    phase: 'library',
    tour: 'hwcg-plan-library',
    title: '4.3 Planning Repository and Pre-Incident Planning',
    description:
      'PRATUS provides a searchable digital repository for USCG-approved plans, forms, templates, job aids, process guides, and reusable incident-management content, plus unit pre-incident preparedness packages and blue-skies IMT readiness outside an active incident.',
  },
  requirement({
    id: 'org004',
    phase: 'library',
    requirementId: 'FOC-ORG-004',
    name: 'Approved Planning Repository',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'hwcg-plan-library',
    description:
      'PRATUS provides a searchable digital repository for USCG-approved plans, forms, templates, job aids, process guides, and reusable incident-management content. Repository content supports pre-incident planning, exercise design, incident initialization, role-based guidance, and authorized PRATUS AI use.',
  }),
  requirement({
    id: 'org004a',
    phase: 'library',
    requirementId: 'FOC-ORG-004a',
    name: 'Repository Metadata and Version Control',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'foc-plan-row',
    description:
      'Repository items include, as applicable, title, owner, approving authority, mission area, Area of Responsibility, geographic applicability, effective date, expiration/review date, version, superseded status, sensitivity/access restrictions, and source. Users can distinguish current approved material from draft, expired, superseded, or reference-only material.',
  }),
  requirement({
    id: 'org004b',
    phase: 'library',
    requirementId: 'FOC-ORG-004b',
    name: 'Search, Filter, and Retrieval',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'foc-plan-search',
    description:
      'Authorized users can search and filter repository content by title, type, mission, geography/AOR, organization, form or plan type, status, owner, and other approved metadata without requiring knowledge of the underlying storage location.',
  }),
  requirement({
    id: 'org004c',
    phase: 'library',
    requirementId: 'FOC-ORG-004c',
    name: 'Instantiate Approved Content',
    disposition: FOC_REQUIRED_DISPOSITION,
    description: INSTANTIATE_PLAN,
  }),
  requirement({
    id: 'org004c',
    phase: 'tasks',
    requirementId: 'FOC-ORG-004c',
    name: 'Instantiate Approved Content',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'hwcg-tasks-apply-plan',
    description: INSTANTIATE_PLAN,
  }),
  requirement({
    id: 'org004d',
    phase: 'library',
    requirementId: 'FOC-ORG-004d',
    name: 'Repository and AI Authority',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'PRATUS AI identifies the authority and version of repository material used in a response or draft. Superseded, draft, exercise-only, or locally developed content is not represented as current Coast Guard doctrine unless explicitly approved and labeled for that purpose.',
  }),
  requirement({
    id: 'org004e',
    phase: 'library',
    requirementId: 'FOC-ORG-004e',
    name: 'Repository Authority Classification',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Repository content is visibly classified by authority and status, including enterprise-approved, unit/local-approved, draft, exercise-only, superseded, and reference-only content as applicable. Local or exercise-derived material is not elevated to enterprise doctrine or presented as Coast Guard-wide approved content unless it has completed the applicable approval process.',
  }),
  {
    id: 'sectionImt',
    phase: 'rosterTemplates',
    tour: 'foc-roster-library',
    title: '4.3.1 Unit Incident Management Preparedness / Manage IMT',
    description:
      'PRATUS provides authorized Coast Guard units a persistent blue-skies incident-management preparedness capability outside an active incident or exercise, including reusable IMT organizations, personnel pools, staffing gap views, and draft activation lists.',
  },
  requirement({
    id: 'org005',
    phase: 'rosterTemplates',
    requirementId: 'FOC-ORG-005',
    name: 'Unit IMT Organization and Personnel Readiness',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'foc-roster-library',
    description:
      'PRATUS provides authorized Coast Guard units a persistent blue-skies incident-management preparedness capability outside an active incident or exercise. The capability allows units to maintain reusable IMT organizations, identify personnel available for anticipated ICS roles, understand staffing depth and gaps, and keep preparedness information current so the unit can activate more quickly during an exercise or real-world response. This capability supports incident-management readiness and does not replace authoritative Coast Guard personnel, manpower, qualification, or training systems.',
  }),
  requirement({
    id: 'org005a',
    phase: 'rosterTemplates',
    requirementId: 'FOC-ORG-005a',
    name: 'Pre-Scripted IMT Organization Templates',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'foc-roster-new',
    description:
      'Authorized unit managers can create, save, copy, revise, and retire reusable IMT organization templates using IMH-aligned structures, Coast Guard-specific structures, specialty positions, local configuration, deputies, technical specialists, Agency Representatives, and authorized custom positions. Templates can be unstaffed, partially staffed, or pre-associated with current unit personnel.',
  }),
  requirement({
    id: 'org005b',
    phase: 'rosterTemplates',
    requirementId: 'FOC-ORG-005b',
    name: 'Mission-, Hazard-, and Contingency-Specific Organizations',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'foc-roster-row',
    description:
      'A unit can maintain multiple organization templates for different anticipated uses without overwriting one another, such as hurricane response, marine environmental response, mass rescue operations, major public events, continuity operations, or other locally approved contingencies. Each template identifies its owning unit, intended use, version, and approval status.',
  }),
  requirement({
    id: 'org005c',
    phase: 'rosterTemplates',
    requirementId: 'FOC-ORG-005c',
    name: 'Unit IMT Personnel Pool',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Authorized unit users can maintain an incident-management personnel pool for blue-skies preparedness. The pool can identify current unit personnel, contact information available from the user profile, locally planned primary and alternate ICS roles, availability/readiness for IMT use, and approved references to qualifications or credentials where authorized. PRATUS does not independently certify qualifications or convert local role planning into an official qualification determination.',
  }),
  requirement({
    id: 'org005d',
    phase: 'rosterTemplates',
    requirementId: 'FOC-ORG-005d',
    name: 'Personnel Arrival, Transfer, and Departure Lifecycle',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Authorized unit managers can add newly arriving personnel to the unit IMT pool, update anticipated roles or availability as experience and staffing change, and mark personnel as transferred, departed, or otherwise unavailable to the unit. Changes to current unit affiliation or preparedness availability do not delete or rewrite historical incident, exercise, activity-log, evaluation, or other records associated with that person.',
  }),
  requirement({
    id: 'org005e',
    phase: 'rosterTemplates',
    requirementId: 'FOC-ORG-005e',
    name: 'Staffing Coverage and Gap View',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'PRATUS can compare a selected pre-scripted IMT organization with the unit IMT personnel pool and display planned primary coverage, alternate/depth coverage, vacant positions, and positions that may require augmentation from another unit or echelon. The display clearly distinguishes local preparedness assignments from formal qualification or certification status.',
  }),
  requirement({
    id: 'org005f',
    phase: 'rosterTemplates',
    requirementId: 'FOC-ORG-005f',
    name: 'Draft Activation and Recall List',
    disposition: FOC_REQUIRED_DISPOSITION,
    description: INSTANTIATE_TEMPLATE,
  }),
  requirement({
    id: 'org005f',
    phase: 'roster',
    requirementId: 'FOC-ORG-005f',
    name: 'Draft Activation and Recall List',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'foc-roster-load',
    description: INSTANTIATE_TEMPLATE,
  }),
  requirement({
    id: 'org005g',
    phase: 'rosterTemplates',
    requirementId: 'FOC-ORG-005g',
    name: 'Organizational Ownership and Permissions',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      "Unit-level preparedness content is managed through role-based permissions. Authorized unit managers can create and maintain their unit's templates and personnel pool; higher echelons or program administrators can view, approve, share, or govern content within their authorized scope. Changes to templates, staffing designations, and preparedness records are auditable.",
  }),
  requirement({
    id: 'org005h',
    phase: 'rosterTemplates',
    requirementId: 'FOC-ORG-005h',
    name: 'Personnel-System Boundary',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'The unit IMT personnel pool is an incident-management preparedness aid and is not the authoritative source for permanent duty assignment, PCS orders, pay/personnel actions, medical readiness, workforce management, or formal ICS qualification/certification. Approved authoritative data feeds may be used to update organizational affiliation or other permitted data when available, but the unit preparedness workflow remains usable without requiring a new personnel-system integration.',
  }),
  requirement({
    id: 'org005i',
    phase: 'rosterTemplates',
    requirementId: 'FOC-ORG-005i',
    name: 'Unit Preparedness Readiness Summary',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'The unit workspace provides an incident-management readiness summary showing, as applicable, the current approved IMT templates, template review dates, planned staffing gaps, primary/alternate coverage, last exercise or validation date, packages requiring review, and other preparedness actions. The summary is limited to incident-management readiness and does not become a general Coast Guard workforce-readiness system.',
  }),
  requirement({
    id: 'org006',
    phase: 'packages',
    requirementId: 'FOC-ORG-006',
    name: 'Unit Pre-Incident Planning and IAP Preparedness Packages',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'hwcg-plan-library',
    description:
      'PRATUS allows authorized units to build and maintain reusable pre-incident preparedness packages that reduce the time needed to establish an exercise or real-world incident. These packages are controlled planning resources, not pre-approved operational IAPs, and must enter the normal incident-specific review and approval workflow when activated.',
  }),
  requirement({
    id: 'org006a',
    phase: 'packages',
    requirementId: 'FOC-ORG-006a',
    name: 'Preparedness Package Content',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'A unit preparedness package can contain, as applicable, a pre-scripted IMT organization, locally approved objective/strategy/tactic libraries, IAP component selections, meeting schedule or battle rhythm, planned facilities and locations, map views/layers, communications planning assumptions, medical and safety planning assumptions, resource assumptions, partner/contact lists, contingency plans, checklists, reference plans/forms, and other approved reusable incident-management content. Not every package is required to contain every element.',
  }),
  requirement({
    id: 'org006b',
    phase: 'packages',
    requirementId: 'FOC-ORG-006b',
    name: 'Pre-Scripted Objectives, Strategies, and Tactics',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Authorized units can maintain locally approved objective, strategy, and tactic examples tagged by mission, hazard, geography/AOR, season, or contingency. When reused in an incident or exercise, these items begin as proposed planning content and do not bypass command review, operational validation, or approval.',
  }),
  requirement({
    id: 'org006c',
    phase: 'packages',
    requirementId: 'FOC-ORG-006c',
    name: 'Pre-Scripted IAP Components and Prior IAP References',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Units can maintain reusable IAP component configurations, draft/pre-scripted form content, and approved prior exercise IAPs as preparedness references. PRATUS clearly distinguishes a template, pre-plan, or exercise IAP from an approved real-world IAP and does not carry prior signatures or approvals forward as current operational approval.',
  }),
  requirement({
    id: 'org006d',
    phase: 'packages',
    requirementId: 'FOC-ORG-006d',
    name: 'Higher-Echelon and Enterprise Reuse With Local Adaptation',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Authorized units can copy approved enterprise or higher-echelon preparedness content into a local unit package when permitted. The local copy retains source/version provenance and can be adapted without altering the authoritative source. Local modifications remain local unless separately approved for broader use.',
  }),
  requirement({
    id: 'org006e',
    phase: 'packages',
    requirementId: 'FOC-ORG-006e',
    name: 'Preparedness Package Governance and Versioning',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Each package identifies the owning organization, approving authority, status, version, effective date when applicable, review/expiration date, last modified date, and source. Authorized users can preserve superseded versions and identify the currently approved unit package.',
  }),
  requirement({
    id: 'org006f',
    phase: 'packages',
    requirementId: 'FOC-ORG-006f',
    name: 'Instantiate a Package Into an Exercise or Incident',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      "During exercise or incident creation, an authorized user can select a unit preparedness package and choose which components to instantiate into the new workspace. The source package remains unchanged, and copied content becomes subject to the exercise or incident's permissions, workflow states, approvals, audit controls, and records requirements.",
  }),
  requirement({
    id: 'org006g',
    phase: 'packages',
    requirementId: 'FOC-ORG-006g',
    name: 'Operational Validation Before Real-World Use',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'When a preparedness package is instantiated into a real-world incident, PRATUS requires validation of time-sensitive and incident-specific content before it is treated as current. Prior personnel assignments, dates, operational periods, approvals/signatures, resource status, current hazards, weather/tides, costs/funding, live communications information, and facility status are reviewed or reset as applicable. This requirement aligns with FOC-EX-001i for exercise-derived content.',
  }),
  requirement({
    id: 'org006h',
    phase: 'packages',
    requirementId: 'FOC-ORG-006h',
    name: 'Blue-Skies Separation From Operational Records',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Creating, editing, reviewing, or approving unit preparedness packages does not create an active incident, count as an active operational incident, commit resources, create real-world tasking, alter a live incident COP, generate operational cost records, or send operational notifications unless an authorized user deliberately activates the content through an exercise or incident workflow.',
  }),
  requirement({
    id: 'org006i',
    phase: 'packages',
    requirementId: 'FOC-ORG-006i',
    name: 'Preparedness Package Review Status',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Authorized unit and higher-echelon users can identify the package owner, mission/hazard applicability, last review date, next review date, last exercise or operational use, current approval status, related IMT template, and outstanding review actions so that outdated preparedness content can be identified before an event occurs.',
  }),
  requirement({
    id: 'org006j',
    phase: 'packages',
    requirementId: 'FOC-ORG-006j',
    name: 'Exercise Feedback Loop to Preparedness Packages',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Approved exercise findings and products can be incorporated into a unit preparedness package through the controlled promotion process described in FOC-EX-001l. The package change history records the source exercise or AAR, the content changed, the approving authority, and the resulting version; exercise feedback does not automatically alter an approved package.',
  }),
  {
    id: 'focFeedback',
    phase: 'packages',
    title: 'FOC Feedback',
    // Prebuilt HTML with a clickable link; toDriveStep must not escape it.
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_PLANNING),
  },
];

/** One id per requirement the walk covers, in the order a user meets them. */
const WALK_ORDER: FocPlanningTourStepId[] = [
  'sectionIntro',
  'org004',
  'org004a',
  'org004b',
  'org004c',
  'org004d',
  'org004e',
  'sectionImt',
  'org005',
  'org005a',
  'org005b',
  'org005c',
  'org005d',
  'org005e',
  'org005f',
  'org005g',
  'org005h',
  'org005i',
  'org006',
  'org006a',
  'org006b',
  'org006c',
  'org006d',
  'org006e',
  'org006f',
  'org006g',
  'org006h',
  'org006i',
  'org006j',
  'focFeedback',
];

export function focPlanningTourStepsForPhase(
  phase: FocPlanningTourPhase
): FocPlanningTourStepDef[] {
  return FOC_PLANNING_TOUR_STEPS.filter((step) => step.phase === phase);
}

export function focPlanningTourStepIds(): FocPlanningTourStepId[] {
  return WALK_ORDER;
}

export function focPlanningTourStepTitles(): string[] {
  return WALK_ORDER.map((id) => {
    const step = FOC_PLANNING_TOUR_STEPS.find((entry) => entry.id === id)!;
    return step.title;
  });
}

export function focPlanningTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function wantsFocPlanningTour(
  param: string | null,
  phase: FocPlanningTourPhase
): boolean {
  if (phase === 'library') return param === '1' || param === 'library';
  return param === phase;
}

export function focPlanningTasksHref(incidentId: string): string {
  return `/iap/${incidentId}/actions?${FOC_PLANNING_TOUR_PARAM}=tasks`;
}

export function focPlanningRosterHref(incidentId: string): string {
  return `/iap/${incidentId}/roster?${FOC_PLANNING_TOUR_PARAM}=roster`;
}

function firstLiveIncidentId(
  incidents: readonly { id: string; archived?: number | boolean }[]
): string | null {
  const live = incidents.find((incident) => !incident.archived);
  return live ? String(live.id) : null;
}

export function resolveFocPlanningTasksHref(
  incidents: readonly { id: string; archived?: number | boolean }[] = []
): string | null {
  const id = firstLiveIncidentId(incidents);
  return id ? focPlanningTasksHref(id) : null;
}

export function resolveFocPlanningRosterHref(
  incidents: readonly { id: string; archived?: number | boolean }[] = []
): string | null {
  const id = firstLiveIncidentId(incidents);
  return id ? focPlanningRosterHref(id) : null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Popover body: the disposition line first, then the requirement text. */
export function focPlanningTourPopoverHtml(
  step: Pick<FocPlanningTourStepDef, 'disposition' | 'description' | 'bullets'>
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}
